import { TerminationDialog, type TerminationActions } from "./TerminationDialog";
import { billingPeriod } from "../../business-operations/billing-period";
import { zodResolver } from "@hookform/resolvers/zod";
import { ApiError } from "@carepass/api-client";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Alert,
  Button,
  DataTable,
  DetailList,
  Pagination,
  Tabs,
} from "../../../ui";
import type { DataColumn } from "../../../ui";
import { HookFormField } from "../../../app/forms/HookFormField";
import { businessCodeLabel, date, money } from "../../workspace/model";
import { Kpi, PageHeader, Panel, StateNotice, statusBadge } from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";
import type { Schemas } from "../../workspace/api.generated";
import {
  memberNoteFormSchema,
} from "../schema";
import type {
  MemberNoteFormValues,
} from "../schema";
import "./MemberTerminationDialog.css";

export type MemberDetailTab = "기본정보" | "약관 동의" | "거래 내역" | "월정액 결제" | "알림 이력" | "상담 메모" | "이용 현황";
type LoadState = "ready" | "loading" | "error";

export interface MemberDetailPermissions {
  readonly revealSensitive: boolean;
  readonly createNote: boolean;
  readonly requestTermination: boolean;
  readonly openRefunds: boolean;
  readonly cancelPayment?: boolean;
  readonly executeTransfer?: boolean;
}

export interface MemberDetailViewProps extends TerminationActions {
  readonly detail?: Schemas["MemberDetailView"];
  readonly state?: LoadState | "not-found";
  readonly tab: MemberDetailTab;
  readonly payments?: Schemas["AdminPageSubscriptionPaymentView"];
  readonly paymentsState?: LoadState;
  readonly onViewTransaction?: (transactionId: number) => void;
  readonly notes?: readonly Schemas["MemberCareNoteView"][];
  readonly notesState?: LoadState;
  readonly history?: Schemas["MemberAdminHistoryView"];
  readonly historyState?: LoadState;
  readonly permissions: MemberDetailPermissions;
  readonly onTabChange: (tab: MemberDetailTab) => void;
  readonly onPaymentPageChange: (page: number) => void;
  readonly onRetryDetail: () => void;
  readonly onRetryPayments: () => void;
  readonly onRetryNotes: () => void;
  readonly onRetryHistory?: () => void;
  readonly onBack: () => void;
  readonly backHref?: string;
  readonly backLabel?: string;
  readonly detailHref?: string;
  readonly refundsHref?: string;
  readonly onOpenRefunds: () => void;
  readonly onReveal?: (input: Schemas["UnmaskMemberRequest"]) => Promise<Schemas["MemberSensitiveView"]>;
  readonly onCreateNote: (input: Schemas["CreateMemberCareNoteRequest"]) => Promise<Schemas["MemberCareNoteView"]>;
  readonly onTerminate: (input: Schemas["RequestTerminationRequest"]) => Promise<Schemas["RefundView"]>;
  readonly onPreviewTermination?: () => Promise<Schemas["TerminationPreviewView"]>;
  readonly onRefreshDetail: () => Promise<Schemas["MemberDetailView"] | undefined>;
  readonly revealing?: boolean;
  readonly creatingNote?: boolean;
  readonly terminating?: boolean;
}

const paymentColumns: readonly DataColumn<Schemas["SubscriptionPaymentView"]>[] = [
  { key: "period", title: "이용 기간", render: row => billingPeriod(row.billingCycleStart,row.billingCycleEnd) },
  { key: "date", title: "결제일시", render: row => row.approvedAt ? <>{date(row.approvedAt)}{row.status !== "APPROVED" ? <><br />{statusBadge(row.status)}</> : null}</> : <>{statusBadge(row.status)} · 승인 기록 없음</> },
  { key: "method", title: "결제수단", render: row => businessCodeLabel(row.paymentMethod) },
  { key: "info", title: "결제정보", render: row => row.providerCode ? <>{businessCodeLabel(row.providerCode)}{row.cardCompanyName ? ` · ${row.cardCompanyName}` : ""} · {row.billingKeyMasked ?? row.instrumentMasked ?? "정보 없음"}</> : row.instrumentMasked ?? "정보 없음" },
  { key: "amount", title: "결제금액", numeric: true, render: row => money(row.approvedAmount) },
];

const agreementColumns: readonly DataColumn<Schemas["MemberAgreementHistoryView"]>[] = [
  { key: "title", title: "약관", render: (row) => row.title },
  { key: "code", title: "코드", render: (row) => row.termCode },
  { key: "version", title: "버전", render: (row) => row.versionNo },
  { key: "status", title: "동의 상태", render: (row) => statusBadge(row.agreed ? "AGREED" : "WITHDRAWN") },
  { key: "date", title: "처리일시", render: (row) => date(row.agreedAt) },
];

const notificationLabels: Readonly<Record<string, string>> = {
  TRANSACTION_CARD_PAYMENT_CANCELED: "거래 카드 결제 취소", TRANSACTION_COMPLETED: "거래 완료", TRANSACTION_COMPLETE: "거래 완료", PAYMENT_REQUEST: "결제 요청", TRANSACTION_APPROVED: "결제 요청", TRANSACTION_REJECTED: "심사 반려", SUPPLEMENT_REQUIRED: "자료 보완", NOTICE: "공지사항", SYSTEM: "시스템", SUBSCRIPTION_PAYMENT: "월정액 결제", SUBSCRIPTION_PAYMENT_FAILED: "결제 실패",
};
const memberNotificationColumns: readonly DataColumn<Schemas["MemberNotificationHistoryView"]>[] = [
  { key: "date", title: "발송일시", render: row => row.sentAt ? date(row.sentAt) : <>{statusBadge(row.deliveryStatus)} · 미발송</> },
  { key: "type", title: "종류", render: row => notificationLabels[row.notificationType] ?? "기타 알림" },
  { key: "title", title: "제목", render: row => row.title },
  { key: "body", title: "내용", render: row => <div className="admin-notification-body">{row.body ?? "내용 없음"}</div> },
];

function safeMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const codeMessages: Readonly<Record<string, string>> = {
      ADMIN_PERMISSION_DENIED: "현재 권한으로 이 작업을 처리할 수 없습니다.",
      MEMBER_NOT_FOUND: "회원을 찾을 수 없습니다. 목록에서 다시 선택해 주세요.",
      MEMBER_UNMASK_REASON_INVALID: "원문 열람 사유를 5~500자로 입력해 주세요.",
      MEMBER_UNMASK_FIELDS_INVALID: "열람할 개인정보 항목을 다시 선택해 주세요.",
      MEMBER_CARE_NOTE_INVALID: "상담 메모 내용을 확인해 주세요.",
    };
    return codeMessages[error.code] ?? (error.kind === "network" ? "네트워크 연결을 확인한 뒤 다시 시도해 주세요." : fallback);
  }
  return fallback;
}

function MemberNotesPanel({
  notes,
  state,
  canCreate,
  creating,
  onCreate,
  onRetry,
}: {
  readonly notes?: readonly Schemas["MemberCareNoteView"][];
  readonly state: LoadState;
  readonly canCreate: boolean;
  readonly creating: boolean;
  readonly onCreate: MemberDetailViewProps["onCreateNote"];
  readonly onRetry: () => void;
}) {
  const form = useForm<MemberNoteFormValues>({
    resolver: zodResolver(memberNoteFormSchema),
    defaultValues: { content: "" },
    shouldFocusError: true,
  });
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const submit = form.handleSubmit(async (values) => {
    if (!canCreate || creating) return;
    setError("");
    setSaved(false);
    try {
      await onCreate({ noteType: "CONSULTATION", content: values.content });
      form.reset();
      setSaved(true);
    } catch (cause) {
      setError(safeMessage(cause, "상담 메모를 저장하지 못했습니다."));
    }
  });

  return (
    <Panel title="상담 메모" description="작성한 메모는 수정하거나 삭제할 수 없습니다." action={<Button size="small" variant="quiet" onClick={onRetry}>최신 메모 조회</Button>}>
      {state === "loading" ? <p role="status">상담 메모를 불러오는 중입니다.</p> : null}
      {state === "error" ? <Alert persistent variant="danger" title="상담 메모를 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={onRetry}>다시 조회</Button>}>잠시 후 다시 조회해 주세요.</Alert> : null}
      {state === "ready" && notes?.length === 0 ? <p>등록된 상담 메모가 없습니다.</p> : null}
      {state === "ready" && notes && notes.length > 0 ? (
        <ol className="admin-note-list">
          {notes.map((note) => (
            <li key={note.id}>
              <p>{note.content}</p>
              <span>{note.noteType} · 관리자 {note.adminLoginId ?? "확인 불가"} · {date(note.createdAt)}</span>
            </li>
          ))}
        </ol>
      ) : null}
      {saved ? <Alert title="상담 메모를 등록했습니다" variant="success">서버에 저장된 최신 메모 목록을 확인했습니다.</Alert> : null}
      {error ? <Alert persistent title="상담 메모 저장 실패" variant="danger">{error}</Alert> : null}
      {canCreate ? (
        <form className="admin-command" noValidate onSubmit={submit}>
          <HookFormField
            compact
            control="textarea"
            formControl={form.control}
            label="상담 메모"
            maxLength={2_000}
            name="content"
            required
          />
          <Button isLoading={creating} size="small" type="submit">메모 추가</Button>
        </form>
      ) : <p>상담 메모 등록 권한이 없습니다.</p>}
    </Panel>
  );
}

export function MemberDetailView({
  detail,
  state = "ready",
  tab,
  payments,
  paymentsState = "ready",
  onViewTransaction,
  notes,
  notesState = "ready",
  history,
  historyState = "ready",
  permissions, onVerifyTerminationAccount, onTerminationBanks, onCurrentTermination, onRetryTermination,
  onTabChange,
  onPaymentPageChange,
  onRetryDetail,
  onRetryPayments,
  onRetryNotes,
  onRetryHistory = () => undefined,
  onBack,
  backHref,
  backLabel,
  detailHref,
  onCreateNote,
  onTerminate,
  onPreviewTermination,
  onRefreshDetail,
  creatingNote = false,
  terminating = false,
}: MemberDetailViewProps) {
  const [terminationOpen, setTerminationOpen] = useState(false);
  const [terminationResult, setTerminationResult] = useState<Schemas["RefundView"]>();
  const terminationTrigger = useRef<HTMLButtonElement>(null);
  const terminationNotice = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!terminationResult) return;
    const frame = window.requestAnimationFrame(() => terminationNotice.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [terminationResult]);

  if (!detail || state !== "ready") {
    return (
      <>
        <PageHeader
          id="ADM-S06"
          title="회원 상세"
          description="회원 상태와 처리 이력을 확인합니다."
          breadcrumbs={[{ label: "회원 관리", href: "/members" }, { label: "회원 상세", href: detailHref ?? "#" }]}
          backAction={backHref ? { label: backLabel ?? "목록으로 돌아가기", href: backHref, onClick: onBack } : undefined}
          action={!backHref ? <Button size="small" variant="quiet" onClick={onBack}>회원 목록으로</Button> : undefined}
        />
        {state === "not-found" ? <StateNotice state="not-found" /> : null}
        {state === "loading" ? <p role="status">회원 정보를 불러오는 중입니다.</p> : null}
        {state === "error" ? <Alert persistent variant="danger" title="회원 정보를 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={onRetryDetail}>다시 조회</Button>}>잠시 후 다시 조회해 주세요.</Alert> : null}
      </>
    );
  }

  const profile = detail.profile;
  const paymentsTableState = paymentsState === "loading" ? "loading" : paymentsState === "error" ? "error" : "ready";
  const historyTableState = historyState === "loading" ? "loading" : historyState === "error" ? "error" : "ready";
  const memberTransactionColumns: readonly DataColumn<Schemas["MemberTransactionHistoryView"]>[] = [
    { key: "number", title: "거래ID", render: row => <Button size="small" variant="quiet" onClick={() => onViewTransaction?.(row.transactionId)} disabled={!onViewTransaction}>{row.displayTransactionNo ?? "표시번호 없음"}</Button> },
    { key: "category", title: "납부종류", render: row => row.categoryName ?? "정보 없음" },
    { key: "amount", title: "금액", numeric: true, render: row => money(row.amount) },
    { key: "status", title: "상태", render: row => statusBadge(row.status) },
    { key: "date", title: "요청일시", render: row => date(row.submittedAt) },
  ];
  const tabs = [
    {
      id: "기본정보",
      label: "기본정보",
      content: (
        <div className="admin-split">
          <Panel title="회원 정보">
            <DetailList items={[
              { label: "생년월일", value: profile.birthDate ?? "정보 없음" },
              { label: "성별", value: profile.genderCode === "MALE" ? "남" : profile.genderCode === "FEMALE" ? "여" : "정보 없음" },
              { label: "이메일", value: profile.email ?? profile.emailMasked ?? "정보 없음" },
              { label: "가입채널", value: detail.channelName ?? businessCodeLabel(detail.channelCode) },
              { label: "가입일", value: date(profile.joinedAt) },
              { label: "최근 로그인", value: detail.lastLoginAt ? date(detail.lastLoginAt) : "기록 없음" },
              { label: "본인인증 상태", value: detail.identityVerificationStatus === "VERIFIED" ? "본인인증 완료" : detail.identityVerificationStatus === "NOT_VERIFIED" ? "미인증" : "정보 없음" },
              { label: "빌키 상태", value: detail.billingKey?.status ? <>{statusBadge(detail.billingKey?.status)} · {businessCodeLabel(detail.billingKey?.providerCode)}</> : "정보 없음" },
            ]} />
          </Panel>
        </div>
      ),
    },
    {
      id: "약관 동의",
      label: "약관 동의",
      content: <Panel title="약관 동의 이력"><DataTable caption="약관 동의 이력" columns={agreementColumns} rows={historyState === "ready" ? history?.termAgreements ?? [] : []} rowKey={(row) => `${row.termCode}:${row.versionNo}`} state={historyTableState} onRetry={onRetryHistory} /></Panel>,
    },
    {
      id: "거래 내역",
      label: "거래 내역",
      content: <Panel title="회원 거래 이력"><DataTable caption="회원 거래 이력" columns={memberTransactionColumns} rows={historyState === "ready" ? history?.transactions ?? [] : []} rowKey={(row) => row.transactionId} state={historyTableState} onRetry={onRetryHistory} /></Panel>,
    },
    {
      id: "월정액 결제",
      label: "월정액 결제",
      content: (
        <Panel title="월정액 결제 이력" description="회원 구독에 청구된 결제만 표시합니다.">
          <DataTable caption="월정액 결제 이력" columns={paymentColumns} rows={paymentsState === "ready" ? payments?.items ?? [] : []} rowKey={(row) => row.id} state={paymentsTableState} onRetry={onRetryPayments} />
          {payments && paymentsState === "ready" ? <Pagination page={payments.page} size={payments.size} totalCount={payments.totalCount} onPageChange={onPaymentPageChange} /> : null}
        </Panel>
      ),
    },
    {
      id: "알림 이력",
      label: "알림 이력",
      content: <Panel title="회원 알림 이력"><DataTable caption="회원 알림 이력" columns={memberNotificationColumns} rows={historyState === "ready" ? history?.notifications ?? [] : []} rowKey={(row) => row.notificationId} state={historyTableState} onRetry={onRetryHistory} /></Panel>,
    },
    {
      id: "상담 메모",
      label: "상담 메모",
      content: <MemberNotesPanel canCreate={permissions.createNote} creating={creatingNote} notes={notes} onCreate={onCreateNote} onRetry={onRetryNotes} state={notesState} />,
    },
    {
      id: "이용 현황",
      label: "이용 현황",
      content: (
        <div className="admin-kpi-grid">
          <Kpi label="거래" value={detail.transactionCount} />
          <Kpi label="문의" value={detail.inquiryCount} />
          <Kpi label="혜택 신청" value={detail.benefitCount} />
        </div>
      ),
    },
  ] satisfies Array<{ id: MemberDetailTab; label: string; content: React.ReactNode }>;

  return (
    <>
      <PageHeader
        id="ADM-S06"
        title="회원 상세"
        description=""
        hideBody
        breadcrumbs={[{ label: "회원 관리", href: "/members" }, { label: "회원 상세", href: detailHref ?? "#" }]}
        backAction={backHref ? { label: backLabel ?? "목록으로 돌아가기", href: backHref, onClick: onBack } : undefined}

        action={<div className="admin-inline-actions">{!backHref ? <Button size="small" variant="quiet" onClick={onBack}>회원 목록으로</Button> : null}<GuidedActionButton blockedReason={!permissions.requestTermination ? "회원 해지 권한이 없습니다. 권한 관리자에게 요청해 주세요." : profile.status === "CLOSED" ? "이미 해지된 회원입니다. 환불 운영에서 후속 처리 상태를 확인해 주세요." : null} onClick={() => setTerminationOpen(true)} ref={terminationTrigger} size="small" variant="danger">{profile.status === "CLOSED" ? "해지 완료" : detail.subscriptionStatus === "TERMINATION_PENDING" ? "해지 처리 상태" : "해지 요청"}</GuidedActionButton></div>}
      />
      <section className="admin-member-summary" aria-label="회원 요약">
        <div><h2>{profile.name ?? profile.nameMasked ?? "이름 정보 없음"}</h2><p>{businessCodeLabel(profile.telecomCode)} · {profile.phoneNumber ?? profile.phoneNumberMasked} · {detail.subscriptionStatus === "TERMINATION_PENDING" && profile.status !== "CLOSED" ? "해지 처리 중" : statusBadge(profile.status, profile.status === "CLOSED" ? "해지" : undefined)}</p></div>
        <DetailList items={[
          { label: "요금제", value: detail.planName ?? "정보 없음" },
          { label: "이번 주기 사용금액", value: detail.limitUsage?.usedAmount == null ? "정보 없음" : money(detail.limitUsage?.usedAmount) },
          { label: "이번 주기 잔액", value: detail.limitUsage?.availableAmount == null ? "정보 없음" : money(detail.limitUsage?.availableAmount) },
        ]} />
      </section>
      {detail.consecutiveFailures != null && detail.consecutiveFailures > 0 ? <Alert persistent variant="warning" title="구독 결제 재시도 예정">연속 {detail.consecutiveFailures}회 실패 · 다음 재시도 {date(detail.nextRetryAt)}</Alert> : null}
      {terminationResult ? <div ref={terminationNotice} tabIndex={-1}><Alert persistent variant={terminationResult.status === "COMPLETED" ? "success" : "warning"} title={terminationResult.status === "COMPLETED" ? "서비스 해지가 완료되었습니다" : "서비스 해지 처리 중입니다"} action={terminationResult.method === "PG_CANCEL" ? <Button size="small" variant="quiet" onClick={() => onTabChange("월정액 결제")}>월정액 결제내역</Button> : permissions.openRefunds ? <a className="admin-row-link" href={`/refunds/${terminationResult.refundId}`}>환불 상세로</a> : undefined}>{terminationResult.refundNo} · {statusBadge(terminationResult.status)} · 고객 환불액 {money(terminationResult.customerTotalRefundAmount)}<br />{terminationResult.calculationText}</Alert></div> : null}
      <Tabs items={tabs} onChange={(value) => onTabChange(value as MemberDetailTab)} value={tab} />
      <TerminationDialog permissions={permissions} onVerifyTerminationAccount={onVerifyTerminationAccount} onTerminationBanks={onTerminationBanks} onCurrentTermination={onCurrentTermination} onRetryTermination={onRetryTermination} detail={detail} onClose={() => setTerminationOpen(false)} onRefresh={onRefreshDetail} onResult={setTerminationResult} onTerminate={onTerminate} onPreview={onPreviewTermination} open={terminationOpen} pending={terminating} triggerRef={terminationTrigger} />
    </>
  );
}
